import React, {useState, useEffect, useMemo, useCallback, useRef, memo} from "react";
import styled from "styled-components";
import {Observer} from "mobx-react";
import {reaction} from "mobx";
import {useAppStore} from "../../../../../StateManager";
import type {Catalog} from "../../../../../data/models/Catalog";
import type {IModel} from "../../../../../data/models/Model";
import type {View} from "../../../../../data/models/View";
import type {Xyicon} from "../../../../../data/models/Xyicon";
import {filterModels} from "../../../../../data/models/filter/Filter";
import {Permission, XyiconFeature} from "../../../../../generated/api/base";
import {DebugInformation} from "../../../../../utils/DebugInformation";
import {TimeUtils} from "../../../../../utils/TimeUtils";
import {StringUtils} from "../../../../../utils/data/string/StringUtils";
import {Debouncer} from "../../../../../utils/function/Debouncer";
import type {ICreateUnplottedXyiconParam} from "../../../../modules/abstract/ModuleView";
import type {SpaceViewRenderer} from "../../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRenderer";
import {ToggleSwitchField} from "../../../../widgets/button/switch/ToggleSwitchField";
import {DraggableXyiconCatalogContainerV5} from "../../../dockable/DraggableXyiconCatalogContainerV5";
import {SearchFieldV5} from "../../../input/search/SearchFieldV5";
import {SelectInputStyled, SelectInputV5} from "../../../input/select/SelectInputV5";
import {VerticalFlex, FlexCenter, VerticalFlexStyle, baseDistance} from "../../../styles/styles";
import CirclePlusIcon from "../../../icons/circle-plus.svg?react";
import type {GridViewV5} from "../../../abstract/GridViewV5";
import {colorPalette} from "../../../styles/colorPalette";
import {ButtonStyled} from "../../../button/ButtonV5";
import {IconButtonV5} from "../../../interaction/IconButtonV5";
import {LoaderStyled, LoaderV5} from "../../../loader/LoaderV5";
import {isSpaceItemCardSupportedForField} from "../../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRendererUtils";
import {doesUserHavePermissionToCreateCatalogItem} from "../../../../modules/catalog/create/CatalogUtils";
import type {DockableTitle} from "./DockableV5";
import {EmptyContainerStyled} from "./BoundaryTypeContainerV5";

const logId: string = "Rendering XyiconCatalogContainer";
const rowPerPage: number = 50;
const leewayBeforeBottom: number = 10;

interface IXyiconCatalogContainerProps {
	readonly spaceViewRenderer: SpaceViewRenderer;
	readonly gridView?: React.RefObject<GridViewV5<IModel>>;
	readonly renderTitle: DockableTitle;
	readonly isDocked: boolean;
	readonly isOpen: boolean;
	readonly onClose: () => void;
	readonly onCloseClick: () => void;
	readonly onAddCatalogClick: (event: React.MouseEvent) => void;
	readonly onDuplicateCatalogClick: (catalog: Catalog) => void;
	readonly onCreateUnplottedXyicons: (params: ICreateUnplottedXyiconParam[]) => Promise<void> | void;
}

export const XyiconCatalogContainerV5 = memo((props: IXyiconCatalogContainerProps) => {
	const appState = useAppStore((state) => state.appState);
	const _debouncer = useRef<Debouncer>(new Debouncer(100));
	const glyphContainerRef = useRef<HTMLDivElement>(null);

	const [search, setSearch] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [onlyFavorites, setOnlyFavorites] = useState<boolean>(false);
	const [filteredItems, setFilteredItems] = useState<(Xyicon | Catalog)[]>([]);
	const [loadMoreCount, setLoadMoreCount] = useState<number>(1);

	const {
		spaceViewRenderer,
		gridView,
		onDuplicateCatalogClick,
		onCreateUnplottedXyicons,
		isDocked,
		isOpen,
		onClose,
		onCloseClick,
		onAddCatalogClick,
		renderTitle,
	} = props;

	const getFeature = () => {
		if (renderTitle === "Catalog") {
			return XyiconFeature.XyiconCatalog;
		} else if (renderTitle === "Unplotted Xyicons") {
			return XyiconFeature.Xyicon;
		}
	};
	const feature = getFeature();

	const {actions, organizationIdByAuthData, user} = appState;
	const ls = appState.app.transport.services.localStorage;

	const keyForLocalStorage = useMemo(() => {
		const module = spaceViewRenderer.isMounted ? "space-editor" : "xyicon-panel";

		return `srv4-org-${organizationIdByAuthData}-${module}-catalog-panel-only-favorites`;
	}, [organizationIdByAuthData, spaceViewRenderer.isMounted]);

	const shouldShowCatalogViewSelector = useCallback(() => {
		const permission = user.getOrganizationPermission(XyiconFeature.XyiconCatalog);

		return feature === XyiconFeature.XyiconCatalog && permission >= Permission.View;
	}, [user, feature]);

	const searchableFields = useMemo(() => {
		return actions.getFieldsByFeature(feature).filter((field) => isSpaceItemCardSupportedForField(field));
	}, [actions, feature]);

	const onSearchInput = useCallback((value: string) => {
		_debouncer.current.debounce(() => setSearch(value));
	}, []);

	const onViewChange = (view: View) => {
		actions.selectView(view);
	};

	const sortFilteredItems = useCallback(
		(a: Catalog | Xyicon, b: Catalog | Xyicon) => {
			const aPermission = actions.getModuleTypePermission(a.typeId, XyiconFeature.Xyicon);
			const bPermission = actions.getModuleTypePermission(b.typeId, XyiconFeature.Xyicon);

			if (aPermission === bPermission) {
				if (a.type.name === b.type.name) {
					return StringUtils.sortIgnoreCase(a.model, b.model);
				}

				return StringUtils.sortIgnoreCase(a.type.name, b.type.name);
			}

			return aPermission < bPermission ? 1 : -1;
		},
		[actions],
	);

	const buildFilteredItems = useCallback(() => {
		const selectedView = actions.getSelectedView(feature);
		const items = feature === XyiconFeature.Xyicon ? actions.getUnplottedXyicons() : actions.getList(feature);

		return filterModels(items, selectedView.filters, appState)
			.filter((catalogOrXyicon: Catalog | Xyicon) => {
				if (!onlyFavorites || catalogOrXyicon.ownFeature !== XyiconFeature.XyiconCatalog) {
					return true;
				}

				return catalogOrXyicon.isFavorite;
			})
			.filter((catalogOrXyicon: Catalog | Xyicon) => {
				const stringsToCheck = [catalogOrXyicon.model, catalogOrXyicon.type.name];

				for (const field of searchableFields) {
					const fieldValue = actions.renderValue(catalogOrXyicon, field.refId);

					if (typeof fieldValue === "string" && fieldValue.length > 0) {
						stringsToCheck.push(fieldValue);
					}
				}

				return stringsToCheck.some((str) => StringUtils.containsIgnoreCase(str, search));
			})
			.toSorted(sortFilteredItems) as (Xyicon | Catalog)[];
	}, [actions, appState, feature, onlyFavorites, search, searchableFields, sortFilteredItems]);

	const onGlyphPointerMove = useCallback(() => {
		if (!isDocked) {
			onClose();
		}
	}, [isDocked, onClose]);

	const onGlyphPointerUp = useCallback(() => {
		if (!isDocked && !isOpen) {
			onCloseClick();
		}
	}, [isDocked, isOpen, onCloseClick]);

	const maxNumberOfItemsToShow = loadMoreCount * rowPerPage;
	const visibleItems = useMemo(() => {
		return filteredItems.slice(0, maxNumberOfItemsToShow).map((item) => ({object: item}));
	}, [filteredItems, maxNumberOfItemsToShow]);

	const onOnlyFavoritesChange = useCallback(
		async (value: boolean) => {
			ls.set(keyForLocalStorage, value);

			setOnlyFavorites(value);

			await TimeUtils.wait(300);

			// TODO
			//_selectInput.current?.setState({open: false});
		},
		[ls, keyForLocalStorage],
	);

	const getOnlyFavorites = useCallback(() => {
		if (feature === XyiconFeature.XyiconCatalog) {
			return (
				<ToggleSwitchField
					classNames="onlyFavoritesToggler hbox alignCenter"
					label="Only Favorites"
					value={onlyFavorites}
					onChange={onOnlyFavoritesChange}
				/>
			);
		}

		return null;
	}, [onlyFavorites, onOnlyFavoritesChange, feature]);

	useEffect(() => {
		setSearch("");
	}, [renderTitle]);

	useEffect(() => {
		return reaction(
			() => {
				const selectedView = actions.getSelectedView(feature);

				return `${selectedView?.id ?? ""}|${JSON.stringify(selectedView?.filters ?? null)}`;
			},
			() => {
				setLoadMoreCount(1);

				if (glyphContainerRef.current) {
					glyphContainerRef.current.scrollTop = 0;
				}
			},
			{fireImmediately: true},
		);
	}, [actions, feature, renderTitle, search, onlyFavorites]);

	useEffect(() => {
		return reaction(
			buildFilteredItems,
			(items) => {
				setFilteredItems(items);
			},
			{fireImmediately: true},
		);
	}, [buildFilteredItems]);

	useEffect(() => {
		const onlyFavorites = ls.get(keyForLocalStorage);
		const timeoutId = window.setTimeout(() => {
			setIsLoading(false);

			requestAnimationFrame(() => DebugInformation.end(logId));
		}, 300); // derived from css: 0.3s transition

		setOnlyFavorites(onlyFavorites ?? false);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [ls, keyForLocalStorage]);

	useEffect(() => {
		const glyphContainer = glyphContainerRef.current;

		if (!glyphContainer || isLoading || filteredItems.length <= maxNumberOfItemsToShow) {
			return;
		}

		const hasVerticalScrollbar = glyphContainer.scrollHeight > glyphContainer.clientHeight;

		if (!hasVerticalScrollbar) {
			setLoadMoreCount((current) => current + 1);
		}
	}, [filteredItems.length, isLoading, maxNumberOfItemsToShow]);

	const onGlyphContainerScroll = useCallback(
		(event: React.UIEvent<HTMLDivElement>) => {
			const {scrollTop, clientHeight, scrollHeight} = event.currentTarget;

			if (scrollTop + clientHeight >= scrollHeight - leewayBeforeBottom && filteredItems.length > maxNumberOfItemsToShow) {
				setLoadMoreCount((current) => current + 1);
			}
		},
		[filteredItems.length, maxNumberOfItemsToShow],
	);

	return (
		<Observer>
			{() => {
				const selectedView = actions.getSelectedView(feature);

				return (
					<XyiconCatalogContainerWrapperStyled>
						<XyiconCatalogContainerStyled>
							<SearchFieldV5
								onInput={onSearchInput}
								value={search}
							/>
							{shouldShowCatalogViewSelector() && (
								<FlexCenter
									$gap="8px"
									$wrap={true}
									style={{minWidth: 0}}
								>
									<SelectInputV5
										childBeforeOptions={getOnlyFavorites()}
										options={actions.getViews(feature)}
										selected={selectedView}
										renderOptionWhenClosed={(view) => <span>{view.name}</span>}
										onChange={onViewChange}
										isSameWidth={true}
									/>
									{doesUserHavePermissionToCreateCatalogItem(appState.user) && (
										<IconButtonV5
											onClick={onAddCatalogClick}
											title="Create Catalog Item"
											IconComponent={CirclePlusIcon}
										/>
									)}
								</FlexCenter>
							)}
						</XyiconCatalogContainerStyled>
						{isLoading ? (
							<LoaderV5 label={`Loading ${feature === XyiconFeature.Xyicon ? "Unplotted Xyicons" : "Catalog"}...`} />
						) : (
							<div
								className="glyphContainer"
								ref={glyphContainerRef}
								onScroll={onGlyphContainerScroll}
							>
								{filteredItems.length > 0 ? (
									<>
										<ResultsCountLine>{`${filteredItems.length} results`}</ResultsCountLine>
										<VerticalFlex
											$gap={baseDistance.sm}
											style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))"}}
										>
											<DraggableXyiconCatalogContainerV5
												spaceViewRenderer={spaceViewRenderer}
												gridView={gridView}
												feature={feature}
												items={visibleItems}
												onPointerMove={onGlyphPointerMove}
												onPointerUp={onGlyphPointerUp}
												onDuplicateCatalogClick={onDuplicateCatalogClick}
												onCreateUnplottedXyicons={onCreateUnplottedXyicons}
												queryString={search}
											/>
										</VerticalFlex>
									</>
								) : (
									<EmptyContainerStyled>
										There are no {feature === XyiconFeature.Xyicon ? "unplotted xyicons" : "catalog items"} to display
									</EmptyContainerStyled>
								)}
							</div>
						)}
					</XyiconCatalogContainerWrapperStyled>
				);
			}}
		</Observer>
	);
});

const ResultsCountLine = styled.small`
	display: block;
	box-sizing: border-box;
	max-width: 100%;
	padding: 0 ${baseDistance.md};
	color: ${colorPalette.gray.c500Primary};
	white-space: normal;
	overflow-wrap: break-word;
`;

const XyiconCatalogContainerWrapperStyled = styled.div`
	${VerticalFlexStyle};
	flex: 1;
	min-width: 0;
	min-height: 0;
	gap: ${baseDistance.md};

	${LoaderStyled} {
		margin-bottom: ${baseDistance.md};
	}

	.glyphContainer {
		${VerticalFlexStyle};
		gap: ${baseDistance.xs};
		flex: 1;
		min-height: 0;
		overflow-y: auto;
	}
`;

const XyiconCatalogContainerStyled = styled.div`
	box-sizing: border-box;
	min-width: 0;
	gap: ${baseDistance.sm};
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(min(100%, 160px), 1fr));
	padding: 0 ${baseDistance.md};

	.SearchField {
		width: 100%;
		height: 32px;
	}

	${SelectInputStyled} {
		flex: 1;
		min-width: 0;

		svg {
			min-width: 24px;
		}
	}

	${ButtonStyled} {
		background-color: ${colorPalette.white};
		padding: ${baseDistance.xs};

		svg {
			width: 24px;
			height: 24px;
		}

		svg path {
			stroke: black;
		}

		&:hover {
			background-color: ${colorPalette.gray.c200Light};
		}
	}
`;
